// src/pages/LoyaltyProgram.jsx — Apple-Inspired Redesign
import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getSalonConfig } from '../api/salonConfigService';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import {
  calculatePointsLast365Days,
  determineLoyaltyTier,
  getLoyaltyTierSettings,
  getDefaultTiers
} from '../utils/updateCustomerMetrics';
import '../styles/loyalty.css';

const getFontSizeValue = (fontSize) => {
  switch (fontSize) {
    case 'small':  return '14px';
    case 'large':  return '18px';
    default:       return '16px';
  }
};

// ── Monochromatic icons (all use currentColor) ──────────────────────────────

const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8"  y1="2" x2="8"  y2="6"/>
    <line x1="3"  y1="10" x2="21" y2="10"/>
  </svg>
);

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const StarIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const GiftIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 12 20 22 4 22 4 12"/>
    <rect x="2" y="7" width="20" height="5"/>
    <line x1="12" y1="22" x2="12" y2="7"/>
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

// ── Tier colour palette ──────────────────────────────────────────────────────
// Maps each standard tier name to a solid accent colour used for:
//   • The tier badge pill on the hero card
//   • The filled circle for the current tier on the progress track
// Text colour is chosen for legibility against the badge background.
const TIER_PALETTE = {
  Bronze:   { bg: '#CD7F32', text: '#fff' },
  Silver:   { bg: '#8A8FA8', text: '#fff' },
  Gold:     { bg: '#C9A96E', text: '#fff' },
  Platinum: { bg: '#7B9DB4', text: '#fff' },
};

/** Returns the tier colour config, falling back to a neutral default. */
const getTierColour = (tierName) =>
  TIER_PALETTE[tierName] || { bg: 'rgba(255,255,255,0.25)', text: '#fff' };

// ── Component ────────────────────────────────────────────────────────────────

function LoyaltyProgram() {
  const { salonId } = useParams();
  const navigate    = useNavigate();
  const location    = useLocation();
  const { currentCustomer } = useCustomerAuth();

  const [promotions,        setPromotions]        = useState([]);
  const [vouchers,          setVouchers]          = useState([]);
  const [customerProfile,   setCustomerProfile]   = useState(null);
  const [tierSettings,      setTierSettings]      = useState(getDefaultTiers());
  const [loyaltyEarningRule,setLoyaltyEarningRule]= useState(null);
  const [birthdayBonus,     setBirthdayBonus]     = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState('');
  const [successMessage,    setSuccessMessage]    = useState('');
  const [copiedCode,        setCopiedCode]        = useState('');

  const pointsBalanceRef     = useRef(null);
  const availablePromoRef    = useRef(null);
  const redemptionHistoryRef = useRef(null);

  // Scroll to section when navigated from CustomerNavigation sub-tabs
  useEffect(() => {
    const section = location.state?.section;
    if (!section || loading) return;
    const refMap = {
      'points-balance':       pointsBalanceRef,
      'available-promotions': availablePromoRef,
      'redemption-history':   redemptionHistoryRef,
    };
    const target = refMap[section];
    if (target?.current) {
      setTimeout(() => {
        const headerBar = document.querySelector('.customer-header-bar');
        const tabBar    = document.querySelector('.customer-top-tab-bar');
        const offset    = (headerBar?.offsetHeight || 0) + (tabBar?.offsetHeight || 0);
        const top       = target.current.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }, 150);
    }
  }, [location.state?.section, loading]);

  const showMessage = (message, isError = false) => {
    if (isError) { setError(message); setSuccessMessage(''); }
    else         { setSuccessMessage(message); setError(''); }
    setTimeout(() => { setError(''); setSuccessMessage(''); }, 3000);
  };

  // Load salon config and apply typography + CSS custom properties
  useEffect(() => {
    const loadSalonTypography = async () => {
      try {
        const salonConfig = await getSalonConfig(salonId);
        if (salonConfig?.branding) {
          const root = document.documentElement;
          if (salonConfig.branding.colors) {
            root.style.setProperty('--salon-primary',    salonConfig.branding.colors.primary    || '#5b21b6');
            root.style.setProperty('--salon-secondary',  salonConfig.branding.colors.secondary  || '#ddd6fe');
            root.style.setProperty('--salon-accent',     salonConfig.branding.colors.accent     || '#F6F6F6');
            root.style.setProperty('--salon-background', salonConfig.branding.colors.background || '#FFFFFF');
            root.style.setProperty('--salon-text',       salonConfig.branding.colors.text       || '#333333');
          }
          if (salonConfig.branding.typography) {
            root.style.setProperty('--salon-heading-font', salonConfig.branding.typography.headingFont || 'Familjen Grotesk, sans-serif');
            root.style.setProperty('--salon-body-font',    salonConfig.branding.typography.bodyFont    || 'Wix Madefor Text, sans-serif');
            root.style.setProperty('--salon-font-size',    getFontSizeValue(salonConfig.branding.typography.fontSize));
          }
        }
      } catch (err) {
        console.error('Error loading salon typography:', err);
      }
    };
    if (salonId) loadSalonTypography();
  }, [salonId]);

  // Check if customer matches promotion targeting criteria
  const doesCustomerMatchTargeting = (promotion, customer) => {
    if (!promotion.targetingType || promotion.targetingType === 'all_customers') return true;
    const criteria = promotion.targetingCriteria;

    if (promotion.targetingType === 'customer_profile') {
      if (criteria.birthMonths?.length > 0) {
        if (!customer.birthMonth || !criteria.birthMonths.includes(customer.birthMonth)) return false;
      }
      if (criteria.postalCodes?.length > 0) {
        const cp = customer.postalCode || '';
        if (!criteria.postalCodes.some(c => cp.includes(c))) return false;
      }
    }

    if (promotion.targetingType === 'customer_transaction') {
      const txCount   = customer.metrics?.totalTransactions || 0;
      const totalSpent= customer.metrics?.totalSpent || 0;
      const lastTx    = customer.metrics?.lastTransaction;
      if (criteria.transactionType === 'less_than'    && txCount >= criteria.transactionCount) return false;
      if (criteria.transactionType === 'equal_to'     && txCount !== criteria.transactionCount) return false;
      if (criteria.transactionType === 'greater_than' && txCount <= criteria.transactionCount) return false;
      if (criteria.transactionAmount > 0) {
        if (criteria.transactionAmountType === 'greater_than' && totalSpent <= criteria.transactionAmount) return false;
        if (criteria.transactionAmountType === 'less_than'    && totalSpent >= criteria.transactionAmount) return false;
      }
      if (lastTx && criteria.lastTransactionMonths) {
        const lastDate  = lastTx.seconds ? new Date(lastTx.seconds * 1000) : new Date(lastTx);
        const monthsAgo = new Date();
        monthsAgo.setMonth(monthsAgo.getMonth() - criteria.lastTransactionMonths);
        if (lastDate > monthsAgo) return false;
      }
    }

    if (promotion.targetingType === 'customer_points') {
      const customerTier   = customer.loyaltyTier || 'Bronze';
      const customerPoints = customer.metrics?.pointsBalance || 0;
      if (criteria.memberTiers?.length > 0 && !criteria.memberTiers.includes(customerTier)) return false;
      if (criteria.pointsAmount > 0) {
        if (criteria.pointsType === 'greater_than' && customerPoints <= criteria.pointsAmount) return false;
        if (criteria.pointsType === 'less_than'    && customerPoints >= criteria.pointsAmount) return false;
      }
    }
    return true;
  };

  const getPromotionStatus = (promotion, voucher = null) => {
    const now       = new Date();
    const startDate = promotion.startDate?.seconds ? new Date(promotion.startDate.seconds * 1000) : new Date(promotion.startDate);
    const endDate   = promotion.endDate?.seconds   ? new Date(promotion.endDate.seconds   * 1000) : new Date(promotion.endDate);
    if (voucher?.status === 'redeemed') return 'Redeemed';
    if (voucher?.status === 'expired')  return 'Expired';
    if (now > endDate)  return 'Expired';
    if (now < startDate) return 'Not Started';
    if (!promotion.isActive) return 'Inactive';
    if (promotion.maxRedemptions && promotion.currentRedemptions >= promotion.maxRedemptions) return 'Fully Redeemed';
    return 'Active';
  };

  const copyVoucherCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      showMessage('Voucher code copied!');
      setTimeout(() => setCopiedCode(''), 2000);
    } catch {
      showMessage('Failed to copy code', true);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatDiscount = (promotion) => {
    if (promotion.type === 'percentage')  return `${promotion.discountValue}% OFF`;
    if (promotion.type === 'fixed_amount') return `$${promotion.discountValue} OFF`;
    if (promotion.type === 'free_gift')   return 'FREE GIFT';
    // voucher type — show dollar value from voucherAmount field
    if (promotion.type === 'voucher' && promotion.voucherAmount) return `$${promotion.voucherAmount} Voucher`;
    return '';
  };

  // Load customer profile and promotions
  useEffect(() => {
    const loadLoyaltyData = async () => {
      if (!currentCustomer || !salonId) { setLoading(false); return; }
      try {
        setLoading(true);
        const customerId    = `${currentCustomer.email}_${salonId}`;
        const customerDocRef = doc(db, 'customers', customerId);
        const customerDoc   = await getDoc(customerDocRef);

        const fetchedTierSettings = await getLoyaltyTierSettings(salonId);
        setTierSettings(fetchedTierSettings);

        try {
          const salonDocRef = doc(db, 'salons', salonId);
          const salonDoc    = await getDoc(salonDocRef);
          if (salonDoc.exists()) {
            const lp = salonDoc.data()?.loyaltyProgram;
            if (lp?.isActive && lp?.pointsPerCurrency && lp?.currencyPerPoint) {
              setLoyaltyEarningRule({ pointsPerCurrency: lp.pointsPerCurrency, currencyPerPoint: lp.currencyPerPoint });
            } else {
              setLoyaltyEarningRule(null);
            }
            const bday = lp?.pointsBonus?.birthday;
            setBirthdayBonus(bday?.enabled && bday?.multiplier > 1 ? bday.multiplier : null);
          }
        } catch (e) {
          console.error('Error loading loyalty earning rules:', e);
          setLoyaltyEarningRule(null);
        }

        if (customerDoc.exists()) {
          const customerData   = customerDoc.data();
          const pointsLast365  = await calculatePointsLast365Days(currentCustomer.email, salonId);
          const calculatedTier = determineLoyaltyTier(pointsLast365, fetchedTierSettings);
          if (calculatedTier !== customerData.loyaltyTier) {
            await updateDoc(customerDocRef, {
              loyaltyTier: calculatedTier,
              'metrics.pointsLast365Days': pointsLast365,
              updatedAt: new Date()
            });
          }
          setCustomerProfile({ ...customerData, loyaltyTier: calculatedTier, pointsLast365Days: pointsLast365 });
        } else {
          throw new Error('Customer profile not found');
        }

        const promotionsQuery = query(
          collection(db, 'promotions'),
          where('salonId', '==', salonId),
          where('isActive', '==', true)
        );
        const promotionsSnap = await getDocs(promotionsQuery);
        const allPromotions  = [];
        promotionsSnap.forEach(d => allPromotions.push({ id: d.id, ...d.data() }));

        const customerDocSnap = await getDoc(doc(db, 'customers', `${currentCustomer.email}_${salonId}`));
        const eligible = allPromotions.filter(p => doesCustomerMatchTargeting(p, customerDocSnap.data()));
        setPromotions(eligible);
      } catch (err) {
        console.error('Error loading loyalty data:', err);
        setError('Failed to load loyalty data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadLoyaltyData();
  }, [currentCustomer, salonId]);

  // Real-time vouchers listener
  useEffect(() => {
    if (!currentCustomer || !salonId) return;
    const vouchersQuery = query(
      collection(db, 'vouchers'),
      where('customerEmail', '==', currentCustomer.email),
      where('salonId', '==', salonId)
    );
    const unsubscribe = onSnapshot(vouchersQuery, (snap) => {
      const list = [];
      snap.forEach(d => {
        const v = d.data();
        list.push({
          id: d.id, ...v,
          createdAt:  v.createdAt?.seconds  ? new Date(v.createdAt.seconds  * 1000) : v.createdAt,
          startDate:  v.startDate?.seconds  ? new Date(v.startDate.seconds  * 1000) : v.startDate,
          endDate:    v.endDate?.seconds    ? new Date(v.endDate.seconds    * 1000) : v.endDate,
          redeemedAt: v.redeemedAt?.seconds ? new Date(v.redeemedAt.seconds * 1000) : v.redeemedAt,
        });
      });
      setVouchers(list);
    }, (err) => {
      console.error('Vouchers listener error:', err);
      setError('Failed to load vouchers in real-time');
    });
    return () => unsubscribe();
  }, [currentCustomer, salonId]);

  // ── Drag-to-scroll for voucher carousel (desktop mouse) ─────────────────
  const handleVoucherCarouselMouseDown = (e) => {
    const el = e.currentTarget;
    el.dataset.dragging = 'false';
    const startX     = e.pageX - el.offsetLeft;
    const scrollLeft = el.scrollLeft;
    const onMouseMove = (ev) => {
      const x    = ev.pageX - el.offsetLeft;
      const walk = (x - startX) * 1.2;
      if (Math.abs(walk) > 5) el.dataset.dragging = 'true';
      el.scrollLeft = scrollLeft - walk;
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);
  };

  // ── Tier progress helper ─────────────────────────────────────────────────
  const getTierProgress = (points, tiers) => {
    if (!tiers?.length) return null;
    const sorted     = [...tiers].sort((a, b) => a.minPoints - b.minPoints);
    const currentIdx = sorted.findIndex(t => {
      const max = t.maxPoints === null ? Infinity : t.maxPoints;
      return points >= t.minPoints && points <= max;
    });
    if (currentIdx === -1 || currentIdx === sorted.length - 1) return null;
    const current = sorted[currentIdx];
    const next    = sorted[currentIdx + 1];
    const range   = next.minPoints - current.minPoints;
    const earned  = points - current.minPoints;
    return {
      nextTier: next.name,
      needed:   next.minPoints - points,
      pct:      Math.min(Math.round((earned / range) * 100), 100),
    };
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="ap-loyalty-container loyalty-program">
        <div className="skel-line skel-line--md ap-skel-title" />
        <div className="skel-hero-bar" style={{ height: 168, borderRadius: 22 }} />
        <div className="skel-section" style={{ borderRadius: 18 }}>
          <div className="skel-line skel-line--sm" style={{ marginBottom: 20 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {[0,1,2,3].map(i => (
              <React.Fragment key={i}>
                {i > 0 && <div className="skel-line skel-line--full" style={{ height: 2, flex: 1 }} />}
                <div className="skel-avatar" style={{ width: 20, height: 20, flexShrink: 0 }} />
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="skel-section" style={{ borderRadius: 18 }}>
          <div className="skel-line skel-line--sm" style={{ marginBottom: 16 }} />
          {[1, 2].map(i => (
            <div key={i} className="skel-appt-card" style={{ marginBottom: 12, height: 130, borderRadius: 16 }}>
              <div className="skel-lines" style={{ width: '100%' }}>
                <div className="skel-line skel-line--lg" />
                <div className="skel-line skel-line--md" />
                <div className="skel-line skel-line--full" style={{ height: 7 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Derived render values ────────────────────────────────────────────────
  const tier          = customerProfile?.loyaltyTier || 'Bronze';
  const points        = customerProfile?.metrics?.pointsBalance || 0;
  const progress      = customerProfile ? getTierProgress(points, tierSettings) : null;
  const sortedTiers   = [...tierSettings].sort((a, b) => a.minPoints - b.minPoints);
  const currentTierIdx= sortedTiers.findIndex(t => t.name === tier);

  // Build merged promotions list
  const seenIds          = new Set();
  const mergedPromotions = [];

  vouchers.forEach(voucher => {
    const pid = voucher.promotionId;
    if (pid && !seenIds.has(pid)) {
      seenIds.add(pid);
      const full = promotions.find(p => p.id === pid);
      mergedPromotions.push(full
        ? { promotion: full, voucher, source: 'voucher' }
        : {
            promotion: {
              id: pid,
              name: voucher.promotionName || 'Promotion',
              description: voucher.description || '',
              type: voucher.type || '',
              discountValue: voucher.discountValue || 0,
              freeGiftDescription: voucher.freeGiftDescription || '',
              voucherCode: voucher.voucherCode || '',
              startDate: voucher.startDate,
              endDate: voucher.endDate,
              isActive: true,
              maxRedemptions: null,
              currentRedemptions: 0
            },
            voucher,
            source: 'voucher'
          }
      );
    }
  });

  promotions.forEach(promotion => {
    if (!seenIds.has(promotion.id)) {
      seenIds.add(promotion.id);
      const mv = vouchers.find(v => v.voucherCode === promotion.voucherCode || v.promotionId === promotion.name);
      mergedPromotions.push({ promotion, voucher: mv || null, source: 'targeting' });
    }
  });

  const redeemedVouchers = vouchers.filter(v => v.status === 'redeemed');

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="ap-loyalty-container loyalty-program">
      <main className="ap-loyalty-main">

        {/* Page title box — matches booking-header style */}
        <div className="ap-page-header">
          <h1 className="ap-page-title">Points & Rewards</h1>
        </div>

        {/* ── Membership tier progression track ── */}
        {sortedTiers.length > 0 && (
          <section className="ap-section">
            <h2 className="ap-section-title">Membership Tiers</h2>
            <div className="ap-tier-track">
              {sortedTiers.map((t, i) => {
                const isPast    = i < currentTierIdx;
                const isCurrent = i === currentTierIdx;
                return (
                  <div
                    key={t.name}
                    className={`ap-ts${isCurrent ? ' ap-ts--current' : ''}${isPast ? ' ap-ts--past' : ''}`}
                  >
                    {/* Dot + half-connector row */}
                    <div className="ap-ts-row">
                      <div className={`ap-ts-line ap-ts-line--before${currentTierIdx >= i ? ' ap-ts-line--filled' : ''}`} />
                      <div
                        className="ap-ts-dot"
                        style={isCurrent ? { '--tier-dot-color': getTierColour(t.name).bg } : undefined}
                      />
                      <div className={`ap-ts-line ap-ts-line--after${currentTierIdx > i ? ' ap-ts-line--filled' : ''}`} />
                    </div>
                    {/* Label */}
                    <div className="ap-ts-info">
                      <span className="ap-ts-name">{t.name}</span>
                      <span className="ap-ts-pts">
                        {t.maxPoints
                          ? `${t.minPoints.toLocaleString()}–${t.maxPoints.toLocaleString()}`
                          : `${t.minPoints.toLocaleString()}+`} pts
                      </span>
                      {isCurrent && t.perks && (
                        <span className="ap-ts-perks">{t.perks}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Toast messages */}
        {(error || successMessage) && (
          <div className="ap-toast-container">
            {error && (
              <div className="ap-toast ap-toast--error">
                <span>{error}</span>
                <button onClick={() => setError('')} aria-label="Dismiss">×</button>
              </div>
            )}
            {successMessage && (
              <div className="ap-toast ap-toast--success">
                <span>{successMessage}</span>
                <button onClick={() => setSuccessMessage('')} aria-label="Dismiss">×</button>
              </div>
            )}
          </div>
        )}

        {/* ── Hero membership card ── */}
        {customerProfile && (
          <div className="ap-hero-card" ref={pointsBalanceRef} id="section-points-balance">
            <div className="ap-hero-eyebrow">
              <span
                className="ap-hero-tier-badge"
                style={{ background: getTierColour(tier).bg, color: getTierColour(tier).text, borderColor: 'transparent' }}
              >{tier}</span>
              {customerProfile.createdAt && (
                <span className="ap-hero-since">
                  Member since {new Date(customerProfile.createdAt.seconds * 1000)
                    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
              )}
            </div>
            <div className="ap-hero-points-row">
              <span className="ap-hero-pts-num">{points.toLocaleString()}</span>
              <span className="ap-hero-pts-unit">points</span>
            </div>
            {progress ? (
              <div className="ap-hero-progress">
                <div className="ap-hero-progress-track">
                  <div className="ap-hero-progress-fill" style={{ width: `${progress.pct}%` }} />
                </div>
                <div className="ap-hero-progress-labels">
                  <span>{progress.pct}% to {progress.nextTier}</span>
                  <span>{progress.needed.toLocaleString()} pts away</span>
                </div>
              </div>
            ) : (
              <div className="ap-hero-top-tier">Highest tier achieved</div>
            )}
          </div>
        )}

        {/* ── Available Vouchers ── */}
        <section className="ap-section" ref={availablePromoRef} id="section-available-promotions">
          <h2 className="ap-section-title">Available Vouchers</h2>

          {mergedPromotions.length === 0 ? (
            <div className="ap-empty-state">
              <svg className="ap-empty-icon" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="14" y="28" width="52" height="34" rx="7" stroke="currentColor" strokeWidth="2"/>
                <path d="M14 40h52" stroke="currentColor" strokeWidth="2"/>
                <path d="M30 34v12M50 34v12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="40" cy="22" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M37 22l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p className="ap-empty-title">No vouchers right now</p>
              <p className="ap-empty-sub">Check back soon — exclusive offers are on their way.</p>
            </div>
          ) : (
            <div className="ap-vouchers-grid" onMouseDown={handleVoucherCarouselMouseDown}>
              {mergedPromotions.map(({ promotion, voucher, source }) => {
                const status    = getPromotionStatus(promotion, voucher);
                const isActive  = status === 'Active';
                const statusCls = status.toLowerCase().replace(' ', '-');
                return (
                  <div key={promotion.id} className={`ap-voucher-card ap-voucher-card--${statusCls}`}>

                    {/* Header: discount + status */}
                    <div className="ap-vc-header">
                      <span className="ap-vc-discount">{formatDiscount(promotion)}</span>
                      <span className={`ap-vc-status ap-vc-status--${statusCls}`}>
                        {isActive && <CheckIcon />}
                        {status}
                      </span>
                    </div>

                    {/* Body: name + description only — fixed height keeps footer aligned */}
                    <div className="ap-vc-body">
                      <h3 className="ap-vc-name">{promotion.name}</h3>
                      {promotion.description && (
                        <p className="ap-vc-desc">{promotion.description}</p>
                      )}
                      {promotion.type === 'free_gift' && promotion.freeGiftDescription && (
                        <div className="ap-vc-gift">
                          <GiftIcon />
                          <span>{promotion.freeGiftDescription}</span>
                        </div>
                      )}
                    </div>

                    {/* Dashed tear divider */}
                    <div className="ap-tear" />

                    {/* Footer: validity → code → QR/redeemed → terms → CTA */}
                    <div className="ap-vc-footer">

                      {/* Validity — always first, above Terms & Conditions */}
                      <div className="ap-vc-validity">
                        <CalendarIcon />
                        <span>
                          Valid {formatDate(promotion.startDate)} – {formatDate(promotion.endDate)}
                        </span>
                      </div>

                      {/* Code row — always second, consistent position */}
                      {promotion.voucherCode && (
                        <div className="ap-code-row">
                          <span className="ap-code-label">Code</span>
                          <div className="ap-code-pill">
                            <span className="ap-code-text">{promotion.voucherCode}</span>
                            <button
                              className={`ap-copy-btn${copiedCode === promotion.voucherCode ? ' ap-copy-btn--copied' : ''}`}
                              onClick={() => copyVoucherCode(promotion.voucherCode)}
                              disabled={!isActive}
                              title="Copy code"
                            >
                              {copiedCode === promotion.voucherCode
                                ? <><CheckIcon /><span>Copied</span></>
                                : <><CopyIcon /><span>Copy</span></>}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Redemptions used count */}
                      {promotion.maxRedemptions && (
                        <div className="ap-vc-uses">
                          {promotion.currentRedemptions || 0} / {promotion.maxRedemptions} redemptions used
                        </div>
                      )}

                      {/* Redeemed date */}
                      {voucher?.status === 'redeemed' && (
                        <div className="ap-redeemed-note">
                          <CheckCircleIcon />
                          <span>Used on {formatDate(voucher.redeemedAt)}</span>
                        </div>
                      )}

                      {/* QR code — active vouchers and eligible promotions */}
                      {(() => {
                        if (!isActive) return null;
                        // Real voucher: use its stored ID (voucherId field or Firestore doc id)
                        // Targeting promotion (no voucher doc): use promotion id + voucherCode as QR value
                        const qrId = voucher?.voucherId || voucher?.id || promotion?.id;
                        if (!qrId) return null;
                        let qrValue = '';
                        try {
                          qrValue = JSON.stringify(
                            voucher
                              ? { voucherId:   voucher.voucherId || voucher.id,
                                  customerId:  voucher.customerId || '',
                                  salonId:     salonId || '' }
                              : { promotionId: promotion.id,
                                  voucherCode: promotion.voucherCode || '',
                                  salonId:     salonId || '' }
                          );
                        } catch {
                          return null;
                        }
                        return (
                          <div className="ap-qr-block">
                            <QRCodeSVG
                              value={qrValue}
                              size={110}
                              level="M"
                              bgColor="#FFFFFF"
                              fgColor="#000000"
                              marginSize={1}
                            />
                            <span className="ap-qr-hint">Show to your stylist to redeem</span>
                          </div>
                        );
                      })()}

                      {/* Terms & Conditions — always below validity */}
                      <details className="ap-terms">
                        <summary>Terms & Conditions</summary>
                        <ul>
                          <li>Valid for one-time use only</li>
                          <li>Cannot be combined with other offers</li>
                          <li>Present voucher code at time of service</li>
                          <li>Valid only at this salon location</li>
                          {promotion.maxRedemptions && (
                            <li>Limited to {promotion.maxRedemptions} total redemptions</li>
                          )}
                        </ul>
                      </details>

                      {/* CTA — pinned to bottom via margin-top: auto in CSS */}
                      <button
                        className="ap-cta-btn"
                        onClick={(e) => {
                          if (e.currentTarget.closest('.ap-vouchers-grid')?.dataset.dragging === 'true') return;
                          navigate(
                            `/BespokeBeauty/${salonId}/customer/book`,
                            { state: { promoCode: promotion.voucherCode, promoName: promotion.name } }
                          );
                        }}
                        disabled={!isActive}
                      >
                        {status === 'Redeemed'        ? 'Already Redeemed'     :
                         status === 'Expired'         ? 'Expired'              :
                         status === 'Fully Redeemed'  ? 'No Longer Available'  :
                         status === 'Inactive'        ? 'Unavailable'          :
                         <><span>Use This Offer</span><ArrowRightIcon /></>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Redemption History ── */}
        {redeemedVouchers.length > 0 && (
          <section className="ap-section" ref={redemptionHistoryRef} id="section-redemption-history">
            <h2 className="ap-section-title">Redemption History</h2>
            <div className="ap-history-list">
              {redeemedVouchers.map(voucher => {
                const promotion = promotions.find(p =>
                  p.id  === voucher.promotionId  ||
                  p.name === voucher.promotionName ||
                  p.voucherCode === voucher.voucherCode
                );
                return (
                  <div key={voucher.id} className="ap-history-item">
                    <div className="ap-history-icon">
                      <CheckCircleIcon />
                    </div>
                    <div className="ap-history-body">
                      <div className="ap-history-name">
                        {voucher.promotionName || promotion?.name || 'Promotion'}
                      </div>
                      <div className="ap-history-meta">
                        <span className="ap-history-code">{voucher.voucherCode}</span>
                        <span className="ap-history-sep">·</span>
                        <span className="ap-history-date">Redeemed {formatDate(voucher.redeemedAt)}</span>
                      </div>
                    </div>
                    <div className="ap-history-tag">Used</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── How to Earn Points ── */}
        {(loyaltyEarningRule || birthdayBonus) && (
          <section className="ap-section">
            <h2 className="ap-section-title">How to Earn Points</h2>
            <div className="ap-earn-grid">
              {loyaltyEarningRule && (
                <div className="ap-earn-card">
                  <div className="ap-earn-icon"><StarIcon /></div>
                  <h3 className="ap-earn-name">Every Purchase</h3>
                  <p className="ap-earn-desc">
                    Earn {loyaltyEarningRule.pointsPerCurrency} pt{loyaltyEarningRule.pointsPerCurrency !== 1 ? 's' : ''} for
                    every ${loyaltyEarningRule.currencyPerPoint} spent
                  </p>
                </div>
              )}
              {birthdayBonus && (
                <div className="ap-earn-card">
                  <div className="ap-earn-icon"><GiftIcon /></div>
                  <h3 className="ap-earn-name">Birthday Bonus</h3>
                  <p className="ap-earn-desc">
                    Earn {birthdayBonus}× points on purchases during your birthday month
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

      </main>
    </div>
  );
}

export default LoyaltyProgram;
